import { SyncStatusCode } from '@signage-player/data-store';
import { jitter } from '@signage-player/shared-logic';
/**
 * Cap for initial-fetch stagger across the fleet. Spreads the boot/firmware
 * "all devices wake at once" hammer over ~1s so the API doesn't see a cliff.
 */
const STARTUP_JITTER_MS = 1000;
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));
export class PollingContentSync {
    constructor(apiService, logger, config = {}) {
        this.apiService = apiService;
        this.logger = logger;
        this.config = config;
        this.eventListInterval = null;
        this.deviceInterval = null;
        this.timeInterval = null;
        this.eventListPolling = false;
        this.devicePolling = false;
        this.timePolling = false;
        // Per-channel generation tokens, bumped in destroy(). Each scheduleNext
        // closure captures its own gen at init time; if it observes a different
        // gen on resume, an in-flight fetch must NOT re-schedule — otherwise a
        // setNewIntervals during an in-flight tick produces duplicate timer chains.
        this.eventListGen = 0;
        this.deviceGen = 0;
        this.timeGen = 0;
        this.eventlistCallback = null;
        this.deviceCallback = null;
        this.timeCallback = null;
        this.statusCallback = null;
        this.isSettingIntervals = false;
    }
    async initializeEventlistSync(newDataCallback, fetchImmediately = true) {
        if (!this.config.eventlistPollInterval) {
            return;
        }
        const fetchAndCallback = async () => {
            try {
                const eventlist = await this.apiService.getEventlistsForAllDisplays();
                newDataCallback(eventlist);
            }
            catch (error) {
                if (error.response?.status === 401) {
                    if (this.statusCallback) {
                        this.statusCallback({ code: SyncStatusCode.Unauthorized });
                    }
                    this.logger.warning('[PollingContentSync] Failed to fetch event list: ' + error);
                    return;
                }
                if (!error.response || error.code === 'ECONNREFUSED') {
                    if (this.statusCallback) {
                        this.statusCallback({ code: SyncStatusCode.ConnectionRefused });
                    }
                    return;
                }
                this.logger.warning('[PollingContentSync] Failed to fetch event list: ' + error);
            }
        };
        this.logger.debug('[PollingContentSync] Starting event list polling every ' + this.config.eventlistPollInterval + ' ms');
        if (fetchImmediately) {
            await sleep(Math.random() * STARTUP_JITTER_MS);
            await fetchAndCallback();
        }
        this.eventListPolling = true;
        const myGen = this.eventListGen;
        const scheduleNext = () => {
            if (myGen !== this.eventListGen || !this.eventListPolling)
                return;
            this.eventListInterval = setTimeout(async () => {
                this.eventListInterval = null;
                if (myGen !== this.eventListGen || !this.eventListPolling)
                    return;
                try {
                    await fetchAndCallback();
                }
                catch (e) {
                    // fetchAndCallback already swallows API errors; this guards against
                    // a thrown user callback. Without it, scheduleNext is unreachable
                    // past the throw and polling stops silently.
                    this.logger.warning('[PollingContentSync] event list tick threw: ' + e);
                }
                finally {
                    scheduleNext();
                }
            }, jitter(this.config.eventlistPollInterval));
        };
        scheduleNext();
        this.eventlistCallback = newDataCallback;
    }
    async initializeDeviceSync(newDataCallback, fetchImmediately = true) {
        if (!this.config.devicePollInterval) {
            return;
        }
        const fetchAndCallback = async () => {
            try {
                const device = await this.apiService.getDevice();
                newDataCallback(device);
            }
            catch (error) {
                if (error.response?.status === 401) {
                    if (this.statusCallback) {
                        this.statusCallback({ code: SyncStatusCode.Unauthorized });
                    }
                    this.logger.warning('[PollingContentSync] Failed to fetch device data: ' + error);
                    return;
                }
                if (!error.response || error.code === 'ECONNREFUSED') {
                    if (this.statusCallback) {
                        this.statusCallback({ code: SyncStatusCode.ConnectionRefused });
                    }
                    return;
                }
                this.logger.warning('[PollingContentSync] Failed to fetch device data: ' + error);
            }
        };
        if (fetchImmediately) {
            await sleep(Math.random() * STARTUP_JITTER_MS);
            await fetchAndCallback();
        }
        this.devicePolling = true;
        const myGen = this.deviceGen;
        const scheduleNext = () => {
            if (myGen !== this.deviceGen || !this.devicePolling)
                return;
            this.deviceInterval = setTimeout(async () => {
                this.deviceInterval = null;
                if (myGen !== this.deviceGen || !this.devicePolling)
                    return;
                try {
                    await fetchAndCallback();
                }
                catch (e) {
                    this.logger.warning('[PollingContentSync] device tick threw: ' + e);
                }
                finally {
                    scheduleNext();
                }
            }, jitter(this.config.devicePollInterval));
        };
        scheduleNext();
        this.deviceCallback = newDataCallback;
    }
    async initializeTimeSync(newDataCallback, fetchImmediately = true) {
        if (!this.config.timePollInterval) {
            return;
        }
        const fetchAndCallback = async () => {
            try {
                const currentTime = await this.apiService.getServerTime();
                newDataCallback(currentTime);
            }
            catch (error) {
                this.logger.warning('[PollingContentSync] Failed to fetch server time: ' + error);
            }
        };
        if (fetchImmediately) {
            await sleep(Math.random() * STARTUP_JITTER_MS);
            await fetchAndCallback();
        }
        this.timePolling = true;
        const myGen = this.timeGen;
        const scheduleNext = () => {
            if (myGen !== this.timeGen || !this.timePolling)
                return;
            this.timeInterval = setTimeout(async () => {
                this.timeInterval = null;
                if (myGen !== this.timeGen || !this.timePolling)
                    return;
                try {
                    await fetchAndCallback();
                }
                catch (e) {
                    this.logger.warning('[PollingContentSync] time tick threw: ' + e);
                }
                finally {
                    scheduleNext();
                }
            }, jitter(this.config.timePollInterval));
        };
        scheduleNext();
        this.timeCallback = newDataCallback;
    }
    initializeStatusHandler(statusCallback) {
        this.statusCallback = statusCallback;
    }
    async setNewIntervals(intervals, lastFetchAt) {
        if (this.isSettingIntervals) {
            this.logger.debug('[PollingContentSync] setNewIntervals already in progress, skipping.');
            return;
        }
        this.isSettingIntervals = true;
        try {
            this.destroy();
            this.config.eventlistPollInterval = intervals.eventlistPollInterval;
            this.config.devicePollInterval = intervals.devicePollInterval;
            this.config.timePollInterval = intervals.timePollInterval;
            if (this.eventlistCallback) {
                await this.initializeEventlistSync(this.eventlistCallback, lastFetchAt.eventList ? !this.wasFetchedInLastFiveMinute(lastFetchAt.eventList) : true);
            }
            if (this.deviceCallback) {
                await this.initializeDeviceSync(this.deviceCallback, lastFetchAt.device ? !this.wasFetchedInLastFiveMinute(lastFetchAt.device) : true);
            }
            if (this.timeCallback) {
                await this.initializeTimeSync(this.timeCallback, false);
            }
        }
        finally {
            this.isSettingIntervals = false;
        }
    }
    destroy() {
        this.eventListPolling = false;
        this.devicePolling = false;
        this.timePolling = false;
        // Bump generation tokens BEFORE clearing the timer handles. Any tick
        // currently mid-await will see the gen mismatch when it resumes and
        // refuse to call scheduleNext(), preventing duplicate timer chains
        // when setNewIntervals re-initializes a channel.
        this.eventListGen++;
        this.deviceGen++;
        this.timeGen++;
        if (this.eventListInterval) {
            clearTimeout(this.eventListInterval);
            this.eventListInterval = null;
        }
        if (this.deviceInterval) {
            clearTimeout(this.deviceInterval);
            this.deviceInterval = null;
        }
        if (this.timeInterval) {
            clearTimeout(this.timeInterval);
            this.timeInterval = null;
        }
    }
    wasFetchedInLastFiveMinute(date) {
        const now = new Date();
        now.setMinutes(now.getMinutes() - 5);
        if (now > date) {
            return false;
        }
        return true;
    }
}
//# sourceMappingURL=PollingContentSync.js.map